
import Vue, { PropType } from 'vue';
import { db, functions } from '@/firebase';
import {
	Runnit,
	RunnitNode,
	RunnitNodeDef,
	UserRunnitNodeDefInfo,
} from '@run-diffusion/shared';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	SNACKBAR_STATUS,
} from '@/constants/constants';
import {
	KIT_TYPE,
	PORTAL_LOCATION,
	SELECTED_PROMPT_VIEW,
} from '@/views/Runnits/constants';
import EmptyState from '@/components/states/EmptyState.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import RunnitSettingsDrawer from '@/views/Runnits/RunnitSettingsDrawer.vue';
import ImageGalleryDialog from '@/components/ImageGallery/ImageGalleryDialog.vue';
import SelectedImagesDialog from '@/components/ImageGallery/SelectedImagesDialog.vue';
import RunnitToolsLibraryDialog from '@/views/Runnits/RunnitToolsLibraryDialog.vue';
import KitDialogSelect from '@/views/Runnits/pages/KitPages/KitDialogSelect.vue';

import {
	get$bindFirestoreOptions,
	MyBalanceMixin,
	RunnitRemixMixin,
	TeamUserMixin,
} from '@/mixins';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import ActionsIslandFixedBottom from '@/components/ActionsIslandFixedBottom.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import RunnitDuplicateEditNodeDefInternalEditor
	from "@/views/Runnits/RunnitSettings/internalAdminOnly/RunnitDuplicateEditNodeDefInternalEditor.vue";
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import { ROUTER } from '@/router/constants';
import FeaturedNodeDefResultsContainer from '@/views/Runnits/FeaturedNodeDefResultsContainer.vue';
import PrimaryForm from '@/views/Runnits/RunnitSettings/PromptDrivenForm/PrimaryForm.vue';
import ToolOnboardingTopCard from '@/components/ImageGallery/ToolOnboardingTopCard.vue';
import { KitsMixin } from '@/mixins/KitsMixin';
import moment from 'moment';
import { RUNNIT_LIBRARY_FILTER_LOCATION } from '@/utils';
import RunnitBatchGenerationDrawer from '@/views/Runnits/RunnitBatchProcessing/RunnitBatchGenerationDrawer.vue';
import { isNodeBatchEligible } from '@/utils/runnitBatchVariableFields';

export default Vue.extend({
	name: 'RunnitSingleTool',
	metaInfo () {
		return {
			title: this.currentRunnit ? this.currentRunnit.title : 'Loading...',
		};
	},
	props: {
		currentRunnit: {
			type: Object as PropType<Runnit>,
			required: true,
		},
	},
	data () {
		return {
			RUNNIT_LIBRARY_FILTER_LOCATION: Object.freeze(RUNNIT_LIBRARY_FILTER_LOCATION),
			// Freeze static objects to prevent unnecessary reactivity
			PORTAL_LOCATION: Object.freeze(PORTAL_LOCATION),
			IMAGE_GALLERY_DIALOG_NAV: Object.freeze(IMAGE_GALLERY_DIALOG_NAV),

			addingNewToolLoading: false,

			emptyStatePlaceholders: 2,

			nodeDefInternalEditorDialog: {
				open: false,
				selectedNodeDef: null as RunnitNodeDef | null,
			},
			hasFeaturedNodeDefResults: false,
			batchWizardOpen: false,
			currentUserRunnitNodeDefInfo: null as UserRunnitNodeDefInfo | null,
			lastBoundNodeId: null as string | null,
			// Local cache for kit nodeDefs that may not be present in store yet
		};
	},
	mixins: [
		RunnitsImageSelectMixin,
		TeamUserMixin,
		MyBalanceMixin,
		RunnitRemixMixin,
		KitsMixin,
	],
	computed: {
		...mapState([
			'user',
			'runnitState',
			'runnitNodes',
			'runnitNodesMap',
			'publicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'loadingRunnitNodes',
		]),
		...mapGetters([
			'runnitNodesLimitMap',
		]),
		selectedNode () {
			return this.runnitState.selectedNodeId ? this.runnitNodesMap[this.runnitState.selectedNodeId] : null;
		},
		isToolUnpublished () {
			const node = this.selectedNode;
			if (!node || typeof node.nodeDef === 'string') return false;

			return (!node.nodeDef.isPublished ||
				!node.nodeDef.publishedAt) &&
				!this.user?.isAdmin;
		},
		isToolBlockedForTeamUser () {
			const node = this.selectedNode;
			if (!node || typeof node.nodeDef === 'string') return false;
			return this.isNodeDefBlockedForTeamUser(node.nodeDef);
		},
		canOpenBatchWizard (): boolean {
			return isNodeBatchEligible(this.selectedNode) && this.hasBatchGenerationPermission;
		},
		computedActionIslandWidth () {
			const bp = this.$vuetify.breakpoint;
			if (bp.smOnly) return '60%';
			if (bp.mdOnly) return '45%';
			if (bp.lgAndUp) return '35%';
			return '96.5%';
		},
		computedScrollResultsPaddingX () {
			let paddingX = '16px';

			if (this.$vuetify.breakpoint.smAndDown) {
				paddingX = '16px';
			} else if (this.$vuetify.breakpoint.mdOnly) {
				paddingX = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN ? '50px' : '30px';
			} else if (this.$vuetify.breakpoint.lgOnly) {
				paddingX = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN ? '130px' : '50px';
			} else if (this.$vuetify.breakpoint.xlOnly) {
				paddingX = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN ? '250px' : '150px';

				if (this.$vuetify.breakpoint.width > 2800) {
					paddingX = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN ? '150px' : '150px';
				} else if (this.$vuetify.breakpoint.width > 2500) {
					paddingX = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN ? '150px' : '150px';
				} else if (this.$vuetify.breakpoint.width > 2000) {
					paddingX = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN ? '250px' : '200px';
				}
			}

			return paddingX;
		},
		usePromptDrivenForm () {
			if (this.runnitState?.selectedPromptView !== SELECTED_PROMPT_VIEW.PROMPT_DRIVEN) return false;

			const selectedNodeDef = this.selectedNode?.nodeDef;
			return !!selectedNodeDef?.usePromptDrivenForm;
		},
		toolOnboardingTopCardDismissed () {
			if (!this.user?.id || !this.runnitNodes?.length) return false;

			const dismissedAt = this.currentUserRunnitNodeDefInfo?.onboardingDismissedAt;
			const enabledAt = this.runnitNodes[0]?.nodeDef?.onboardingData?.enabledAt;

			if (!dismissedAt || !enabledAt) return false;

			return moment(dismissedAt.toMillis()).isAfter(moment(enabledAt.toMillis()));
		},
		kitType () {
			const q = this.$route && this.$route.query;
			const kit = (q && (q as any).kit) as string | null;
			return kit && (kit === KIT_TYPE.image || kit === KIT_TYPE.video || kit === KIT_TYPE.imageEdit || kit === KIT_TYPE.videoEdit || kit === KIT_TYPE.upscale || kit === KIT_TYPE['3d']) ? kit : null;
		},
		kitOptions () {
			// Same resolver as RunnitToolsLibraryDialog. This path used to spread
			// a missing nodeDef into a blank clickable card, skip the team-block
			// check, and re-sort by nodeDef.sortOrder; all three disagreed with
			// the dialog. The curated order is now authoritative.
			return this.getResolvedKit(this.kitType)?.tools || [];
		},
	},
	watch: {
		runnitNodes: {
			immediate: true,
			handler (newVal: RunnitNode[], oldVal: RunnitNode[]) {
				if (newVal.length && this.user && this.user.id) {
					const nodeDefId = newVal[0].nodeDefId;
					this.loadCurrentUserRunnitNodeDefInfo(nodeDefId);
					this.onSelectNode(newVal[0].id);
					// Ensure draft runnit node run is bound even when drawer is not rendered
					if (this.lastBoundNodeId !== newVal[0].id) {
						this.bindDraftRunnitNodeRun({ userId: this.user.id, nodeId: newVal[0].id });
						this.lastBoundNodeId = newVal[0].id;
					}
				} else if (this.runnitNodes.length) {
					this.onSelectNode(this.runnitNodes[0].id);
					// Ensure draft runnit node run is bound even when drawer is not rendered
					if (this.lastBoundNodeId !== this.runnitNodes[0].id) {
						this.bindDraftRunnitNodeRun({ userId: this.user.id, nodeId: this.runnitNodes[0].id });
						this.lastBoundNodeId = this.runnitNodes[0].id;
					}
				}

				if (this.runnitNodes.length > 1) {
					console.error('A single tool has multiple nodes');
				}
			},
		},
		selectedNode: {
			immediate: true,
			handler (newVal: RunnitNode) {
				const newNodeDefTitle = newVal?.nodeDef?.title;
				const currentToolName = this.$route.query.toolName;
				if (newNodeDefTitle && currentToolName !== newNodeDefTitle) {
					this.$router.replace({
						...this.$route,
						query: {
							...this.$route.query,
							toolName: newNodeDefTitle,
						},
					});
				}
			},
		},
		'$route.name' (newVal: string, oldVal: string) {
			if (newVal !== oldVal) {
				// Clean up dialog states when route changes
				this.updateRunnitState({
					toolsLibraryDialogOpen: false,
					kitDialogOpen: false,
				});
			}
		},
		canOpenBatchWizard (canOpen: boolean) {
			if (!canOpen) {
				this.batchWizardOpen = false;
			}
		},
	},
	mounted () {
		// Ensure any tool selection dialogs are closed on fresh load of a single-tool page
		this.updateRunnitState({
			toolsLibraryDialogOpen: false,
			kitDialogOpen: false,
		});
	},
	methods: {
		moment,
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
			'setUpsellDialog',
			'bindDraftRunnitNodeRun',
		]),
		async loadCurrentUserRunnitNodeDefInfo (nodeDefId: string) {
			if (!this.user || !this.user.id || !nodeDefId) {
				return;
			}
			const userRunnitNodeDefInfoRef = db.doc(`users/${this.user.id}/runnitNodeDefInfos/${nodeDefId}`);
			await this.$bind(
				'currentUserRunnitNodeDefInfo',
				userRunnitNodeDefInfoRef,
				get$bindFirestoreOptions(),
			);
		},
		onSelectNode (nodeId: string) {
			this.updateRunnitState({
				selectedNodeId: nodeId,
				settingsDrawerOpen: !!nodeId,
			});
		},
		setSettingsDrawerOpen (isOpen: boolean) {
			this.updateRunnitState({
				settingsDrawerOpen: isOpen,
				...(!isOpen && {
					imageGalleryDialogOpen: false,
					promptGeneratorOpen: false,
					imageSelectOpen: false,
					inpaintingEditorOpen: false,
				}),
			});
		},
		openBatchWizard () {
			if (!this.canOpenBatchWizard) return;
			this.batchWizardOpen = true;
		},
		setToolsLibraryDialogOpenState (open: boolean) {
			// Always route through the unified RunnitToolsLibraryDialog —
			// matches the change-tool affordance in PrimaryForm.vue and
			// RunnitSettingsDrawer.vue. The previous `?kit=`-aware branch
			// opened the legacy kit-locked KitDialogSelect, which had no
			// way to switch between kits and trapped the user. Same fix
			// applied here for the action-island "Change" button and the
			// dialog's close-event handler.
			this.updateRunnitState({ toolsLibraryDialogOpen: open });
		},
		toggleAdminNodeDefEditor (selectedNodeDef: RunnitNodeDef) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: true,
				selectedNodeDef,
			};
		},
		onEditDialogClose () {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: false,
				selectedNodeDef: null,
			};
		},
		handleRemixClick (nodeDef, nodeRunId, nodeRun, resultUuid) {
			if (!this.user) {
				const currentPath: string = this.$router.resolve({
					name: ROUTER.RUNNIT_REMIX,
					query: {
						rnrid: nodeRunId,
						ruuid: resultUuid,
						autoRemix: true,
					},
				}).href;
				this.$router.push({
					name: ROUTER.LOGIN,
					query: { redirect: currentPath }
				});
			} else {
				this.openToolWithSameInputs(nodeDef, nodeRun, nodeRunId, nodeRun ? nodeRun.runnitId : null, nodeRun ? nodeRun.nodeId : null);
			}
		},
		async toggleOnboardingDismissed (nodeDefId: string) {
			try {
				if (!this.user || !this.user.id) {
					console.error('User not found');
					this.updateSnackbar({
						show: true,
						status: SNACKBAR_STATUS.ERROR,
						message: 'Unable to update visibility preference',
					});
					return;
				}

				if (!nodeDefId) {
					console.error('Node definition not found');
					this.updateSnackbar({
						show: true,
						status: SNACKBAR_STATUS.ERROR,
						message: 'Unable to update visibility preference',
					});
					return;
				}

				const onboardingDismissed: boolean = this.toolOnboardingTopCardDismissed;
				const userRunnitNodeDefInfoRef = db
					.doc(`users/${this.user.id}/runnitNodeDefInfos/${nodeDefId}`);
				const userRunnitNodeDefInfo: UserRunnitNodeDefInfo = (await userRunnitNodeDefInfoRef.get()).data() as UserRunnitNodeDefInfo;
				await userRunnitNodeDefInfoRef.set({
					...userRunnitNodeDefInfo,
					onboardingDismissedAt: onboardingDismissed ? null : new Date(),
					nodeDefId,
					nodeDef: db.doc(`runnitNodeDefs/${nodeDefId}`)
				});

				// Don't need snackbar, it's too busy
			} catch (e) {
				console.error('Error dismissing onboarding:', e);
				this.updateSnackbar({
					show: true,
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to update visibility preference',
				});
			}
		},
		onKitSelect (nodeDefId: string) {
			// Use existing reroute to create/open single-tool while preserving query
			this.routerPush(this.$route, this.$router, {
				name: 'RunnitReroute',
				query: { ...this.$route.query, toolId: nodeDefId },
			});
			this.updateRunnitState({ kitDialogOpen: false });
		},
		goToRunnitHomePage () {
			this.routerPush(this.$route, this.$router, { name: 'RunnitHomePage' });
		},
		async fixMissingNode () {
			if (!this.currentRunnit || !this.currentRunnit.id || !this.currentRunnit.singleToolNodeDefId) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error, fixing your Board, please try again. If the problem continues please reach out to us with a support ticket',
					show: true,
				});
				return;
			}
			const addNodeToRunnitFunctionRef = functions
				.httpsCallable('addNodeToRunnit');
			const { success } = (await addNodeToRunnitFunctionRef({
				runnitId: this.currentRunnit.id,
				nodeDefId: this.currentRunnit.singleToolNodeDefId,
			})).data;

			if (!success) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error, fixing your Board, please try again. If the problem continues please reach out to us with a support ticket',
					show: true,
				});
				return;
			}
		}
	},
	components: {
		RunnitDuplicateEditNodeDefInternalEditor,
		LoadingState,
		ImageGallery,
		RunnitToolsLibraryDialog,
		KitDialogSelect,
		EmptyState,
		ImageGalleryDialog,
		SelectedImagesDialog,
		RunnitSettingsDrawer,
		ActionsIslandFixedBottom,
		BaseButton,
		FeaturedNodeDefResultsContainer,
		PrimaryForm,
		RunnitBatchGenerationDrawer,
		ToolOnboardingTopCard,
	},
});
